import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import QRCode from 'qrcode';
import { fiscalSponsorshipDefinitiveGuide } from '../content/educational/fiscalSponsorshipDefinitiveGuide';

// Move QR code generation to the top level
let qrCodeHomepage, qrCodeApply, qrCodeDonate;

export const generateFiscalSponsorshipDefinitiveGuide = async () => {
  try {
    console.log('Starting PDF generation for Definitive Guide...');
    
    // Generate all QR codes at the start
    try {
      qrCodeHomepage = await QRCode.toDataURL('https://causeandimpact.org');
      qrCodeApply = await QRCode.toDataURL('https://causeandimpact.org/apply');
      qrCodeDonate = await QRCode.toDataURL('https://causeandimpact.org/donate');
      console.log('QR codes generated successfully');
    } catch (error) {
      console.error('Error generating QR codes:', error);
      throw new Error('Failed to generate QR codes');
    }
    
    // Create new document
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'pt',
      format: 'a4'
    });

    // Initialize constants
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 40;
    const contentWidth = pageWidth - (margin * 2);
    const headerHeight = 50;
    const footerHeight = 30;
    const qrSize = 25;

    // Define colors
    const colors = {
      blue: [37, 99, 235],
      green: [5, 150, 105],
      gray: [31, 41, 55],
      white: [255, 255, 255],
      red: [220, 38, 38],
      lightBlue: [59, 130, 246]
    };

    // Move addCallToAction inside main function with access to all variables
    const addCallToAction = (yPosition) => {
      const ctaHeight = 45;
      const ctaSpacing = 20;

      // Check if CTA will fit on current page
      if (yPosition + ctaHeight + ctaSpacing > pageHeight - footerHeight - 30) {
        doc.addPage();
        addHeader();
        addFooter(doc.internal.getNumberOfPages());
        yPosition = headerHeight + 20;
      }

      // Add some spacing before CTA
      yPosition += ctaSpacing;

      // CTA box
      doc.setFillColor(...colors.blue);
      doc.roundedRect(margin, yPosition, contentWidth, ctaHeight, 6, 6, 'F');

      // CTA text
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(...colors.white);
      doc.text('Want to Support the Revolution?', margin + 15, yPosition + 20);

      // Subtitle
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text('Our causes could use your donated support. Scan to contribute today.', 
        margin + 15, yPosition + 35);

      // Add pre-generated QR code for donations
      doc.addImage(qrCodeDonate, 'PNG', 
        pageWidth - margin - 50, 
        yPosition + 5, 
        35, 
        35
      );

      return yPosition + ctaHeight + ctaSpacing;
    };

    // Validate content
    if (!fiscalSponsorshipDefinitiveGuide) {
      throw new Error('Guide content is missing');
    }

    console.log('Content validated, proceeding with generation...');

    // Update spacing constants
    const spacing = {
      afterHeading: 20,
      afterSubheading: 15,
      betweenSections: 25,
      betweenItems: 12,
      bulletIndent: 15,
      numberIndent: 20,
      afterBulletList: 20,
      afterNumberedList: 20,
      paragraphSpacing: 15,
      lineHeight: 14, // Increased from 8 for better readability
      subIndent: 10
    };

    // Add processList function
    const processList = (text, yPosition) => {
      const lines = text.split('\n');
      let currentY = yPosition;
      let inNumberedList = false;
      let currentIndent = 0;
      
      lines.forEach(line => {
        // Handle bold section headers with colon
        if (line.match(/^\*\*.*\*\*:/)) {
          doc.setFont('helvetica', 'bold');
          const boldText = line.replace(/\*\*/g, '');
          doc.text(boldText, margin + currentIndent, currentY);
          doc.setFont('helvetica', 'normal');
          currentY += spacing.lineHeight;
          inNumberedList = false;
        }
        // Handle numbered items
        else if (line.match(/^\d+\./)) {
          inNumberedList = true;
          const numberPart = line.substring(0, line.indexOf('.') + 1);
          const textPart = line.substring(line.indexOf('.') + 1).trim();
          
          // Handle bold text within numbered items
          if (textPart.includes('**')) {
            const [boldPart, normalPart] = textPart.split('**: ');
            const boldText = boldPart.replace(/\*\*/g, '') + ':';
            
            doc.text(numberPart, margin + currentIndent, currentY);
            doc.setFont('helvetica', 'bold');
            const boldWidth = doc.getTextWidth(boldText);
            doc.text(boldText, margin + spacing.numberIndent, currentY);
            doc.setFont('helvetica', 'normal');
            
            if (normalPart) {
              const wrappedText = doc.splitTextToSize(normalPart, contentWidth - spacing.numberIndent - boldWidth - margin);
              wrappedText.forEach((textLine, idx) => {
                doc.text(textLine, margin + spacing.numberIndent + boldWidth + 5, currentY + (idx * spacing.lineHeight));
              });
              currentY += (wrappedText.length * spacing.lineHeight);
            }
          } else {
            const wrappedText = doc.splitTextToSize(textPart, contentWidth - spacing.numberIndent - margin);
            doc.text(numberPart, margin + currentIndent, currentY);
            wrappedText.forEach((textLine, idx) => {
              doc.text(textLine, margin + spacing.numberIndent, currentY + (idx * spacing.lineHeight));
            });
            currentY += (wrappedText.length * spacing.lineHeight);
          }
        }
        // Handle bullet points (both main and nested)
        else if (line.trim().startsWith('•')) {
          const bulletText = line.substring(1).trim();
          const indentLevel = line.indexOf('•') / 2; // Calculate indent level based on spaces
          const currentBulletIndent = spacing.bulletIndent + (indentLevel * spacing.subIndent);
          
          doc.text('•', margin + currentBulletIndent - spacing.subIndent, currentY);
          const wrappedText = doc.splitTextToSize(bulletText, contentWidth - currentBulletIndent - margin);
          wrappedText.forEach((textLine, idx) => {
            doc.text(textLine, margin + currentBulletIndent, currentY + (idx * spacing.lineHeight));
          });
          currentY += (wrappedText.length * spacing.lineHeight);
        }
        // Handle regular text
        else if (line.trim()) {
          const wrappedText = doc.splitTextToSize(line, contentWidth - currentIndent);
          wrappedText.forEach(textLine => {
            doc.text(textLine, margin + currentIndent, currentY);
            currentY += spacing.lineHeight;
          });
        }
        
        // Add appropriate spacing after each section
        if (line.trim()) {
          currentY += spacing.betweenItems;
        }
      });
      
      return currentY;
    };

    // Font sizes and styles
    const fonts = {
      title: 24,
      subtitle: 18,
      sectionHeading: 16,
      subHeading: 14,
      body: 11,
      footer: 8,
      disclaimer: 9,
      caption: 10
    };

    // Add PDF metadata for SEO
    doc.setProperties({
      title: "The Definitive Guide to Modern Fiscal Sponsorship",
      subject: "Comprehensive guide to modern fiscal sponsorship frameworks, compliance, and innovation",
      author: "Cause & Impact, Inc.",
      keywords: fiscalSponsorshipDefinitiveGuide.keywords.join(", "),
      creator: "Cause & Impact, Inc.",
      producer: "Cause & Impact Fiscal Sponsorship Platform",
      creationDate: new Date(),
      modDate: new Date(),
      trapped: true
    });

    // Add header with SEO-optimized text
    const addHeader = () => {
      try {
        doc.setFillColor(17, 24, 39);
        doc.rect(0, 0, pageWidth, headerHeight, 'F');

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(24);
        
        // Brand emphasis - adjust y-positions
        doc.setTextColor(...colors.blue);
        doc.text('Cause & Impact, Inc.', (pageWidth / 2) + 0.5, 25.1, { align: 'center' });
        
        doc.setTextColor(...colors.green);
        doc.text('Cause & Impact, Inc.', pageWidth / 2, 25, { align: 'center' });

        // Subtitle - adjust font size and y-position
        doc.setFontSize(10);
        doc.setTextColor(...colors.white);
        doc.text('Leading Authority in Modern Fiscal Sponsorship Solutions', pageWidth / 2, 40, { align: 'center' });

        // QR code - adjust size and position
        doc.addImage(qrCodeHomepage, 'PNG', pageWidth - margin - 35, 5, 35, 35);
        console.log('Header added successfully');
      } catch (error) {
        console.error('Error adding header:', error);
        throw error;
      }
    };

    // Add footer with SEO elements
    const addFooter = (pageNumber) => {
      doc.setFillColor(17, 24, 39);
      doc.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, 'F');

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(...colors.white);
      
      // Adjust y-position for footer text
      doc.text(
        'Cause & Impact, Inc. | The Future of Fiscal Sponsorship | www.causeandimpact.org',
        pageWidth / 2,
        pageHeight - 5,
        { align: 'center' }
      );
      doc.text(`Page ${pageNumber}`, 
        pageWidth - margin, 
        pageHeight - 5,
        { align: 'right' }
      );
    };

    // Add title page with SEO optimization
    const addTitlePage = () => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(fonts.title);
      doc.setTextColor(...colors.blue);
      
      let yPosition = headerHeight + 50;
      
      // Main title
      const titleLines = doc.splitTextToSize(
        fiscalSponsorshipDefinitiveGuide.title,
        contentWidth - margin
      );
      titleLines.forEach((line, index) => {
        doc.text(line, pageWidth / 2, yPosition + (index * fonts.title * 0.8), { align: 'center' });
      });
      
      yPosition += (titleLines.length * fonts.title) + 20;

      // Subtitle for SEO
      doc.setFontSize(fonts.subtitle);
      doc.setTextColor(...colors.green);
      const subtitleLines = doc.splitTextToSize(
        fiscalSponsorshipDefinitiveGuide.subtitle,
        contentWidth - margin
      );
      subtitleLines.forEach((line, index) => {
        doc.text(line, pageWidth / 2, yPosition + (index * fonts.subtitle * 0.8), { align: 'center' });
      });

      yPosition += (subtitleLines.length * fonts.subtitle) + 30;

      // Add SEO-rich description
      doc.setFontSize(fonts.body);
      doc.setTextColor(...colors.gray);
      doc.setFont('helvetica', 'normal');
      const descriptionLines = doc.splitTextToSize(
        fiscalSponsorshipDefinitiveGuide.metadata.description,
        contentWidth - margin
      );
      descriptionLines.forEach((line, index) => {
        doc.text(line, pageWidth / 2, yPosition + (index * fonts.body * 1.5), { align: 'center' });
      });

      // Add version and date
      yPosition += (descriptionLines.length * fonts.body * 1.5) + 20;
      doc.setFont('helvetica', 'bold');
      doc.text(
        `${fiscalSponsorshipDefinitiveGuide.version} | ${new Date().toLocaleDateString()}`,
        pageWidth / 2,
        yPosition,
        { align: 'center' }
      );

      return true;
    };

    // Add table of contents with SEO optimization
    const addTableOfContents = (yPosition) => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(fonts.sectionHeading);
      doc.setTextColor(...colors.blue);
      doc.text('Table of Contents', margin, yPosition);
      yPosition += 15;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(fonts.body);
      doc.setTextColor(...colors.gray);

      fiscalSponsorshipDefinitiveGuide.sections.forEach((section, index) => {
        // Add page break if needed
        if (yPosition + 10 > pageHeight - footerHeight - 20) {
          doc.addPage();
          addHeader();
          addFooter(doc.internal.getNumberOfPages());
          yPosition = headerHeight + 20;
        }

        const sectionNum = `${index + 1}.`;
        const pageNum = (index + 3).toString(); // Starting from page 3 after title and TOC

        // Add TOC entry
        doc.setFont('helvetica', 'bold');
        doc.text(sectionNum, margin, yPosition);
        doc.setFont('helvetica', 'normal');
        
        const headingWidth = doc.getTextWidth(sectionNum) + 5;
        const lines = doc.splitTextToSize(section.heading, contentWidth - headingWidth - 30);
        
        // Add dots between title and page number
        const dotsStart = margin + headingWidth + doc.getTextWidth(lines[0]) + 5;
        const dotsEnd = contentWidth - doc.getTextWidth(pageNum) - 15;
        const dotCount = Math.floor((dotsEnd - dotsStart) / 3);
        const dots = '.'.repeat(dotCount);

        lines.forEach((line, idx) => {
          if (idx === 0) {
            // First line includes dots and page number
            doc.text(line, margin + headingWidth, yPosition);
            doc.text(dots, dotsStart, yPosition);
            doc.text(pageNum, contentWidth - 10, yPosition, { align: 'right' });
          } else {
            // Subsequent lines are indented
            doc.text(line, margin + headingWidth, yPosition + (idx * spacing.lineHeight));
          }
        });
        
        yPosition += (lines.length * spacing.lineHeight) + 8;
      });

      return yPosition + 15;
    };

    // Add executive summary with SEO optimization
    const addExecutiveSummary = (yPosition) => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(fonts.sectionHeading);
      doc.setTextColor(...colors.blue);
      doc.text('Executive Summary', margin, yPosition);
      yPosition += 15;

      // Add overview
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(fonts.body);
      doc.setTextColor(...colors.gray);

      const overviewLines = doc.splitTextToSize(
        "This comprehensive guide redefines fiscal sponsorship for the modern era, building upon traditional frameworks while introducing innovative models for scaled impact. It provides authoritative guidance on IRS compliance, risk management, and operational excellence in charitable fund management.",
        contentWidth
      );
      doc.text(overviewLines, margin, yPosition);
      yPosition += (overviewLines.length * spacing.lineHeight) + 15;

      // Add key principles with proper formatting
      doc.setFont('helvetica', 'bold');
      doc.text('Key Principles:', margin, yPosition);
      yPosition += 10;

      const principles = [
        'Absolute Charitable Purpose Alignment',
        'Professional Fund Control & Oversight',
        'Comprehensive Risk Management',
        'Scalable Compliance Systems',
        'Innovation in Philanthropic Structures'
      ];

      doc.setFont('helvetica', 'normal');
      principles.forEach(principle => {
        doc.text(`• ${principle}`, margin + spacing.bulletIndent, yPosition);
        yPosition += spacing.lineHeight;
      });

      return yPosition + 15;
    };

    // Add helper function to process bold text
    const processBoldText = (text, x, y) => {
      // Split text by ** markers
      const parts = text.split(/(\*\*.*?\*\*)/g);
      let currentX = x;
      
      parts.forEach(part => {
        if (part.startsWith('**') && part.endsWith('**')) {
          // Bold text
          doc.setFont('helvetica', 'bold');
          const boldText = part.replace(/\*\*/g, '');
          doc.text(boldText, currentX, y);
          currentX += doc.getTextWidth(boldText);
        } else if (part.trim()) {
          // Normal text
          doc.setFont('helvetica', 'normal');
          doc.text(part, currentX, y);
          currentX += doc.getTextWidth(part);
        }
      });
      
      // Reset font
      doc.setFont('helvetica', 'normal');
      return currentX;
    };

    // Update the section content handling for hybrid frameworks
    const processHybridFrameworks = (yPosition) => {
      // Title with proper bold formatting
      const titleText = "**Hybrid Entity Frameworks**:";
      doc.setTextColor(...colors.gray);
      processBoldText(titleText, margin, yPosition);
      yPosition += spacing.afterSubheading;

      // Numbered items with bold parts
      const frameworks = [
        {
          num: "1.",
          title: "**Enhanced LLC Model**:",
          points: [
            "Single-member LLC with professional management",
            "Multi-layer compliance controls",
            "Automated governance systems"
          ]
        }
        // ... other frameworks
      ];

      frameworks.forEach(framework => {
        // Number and bold title
        const titleX = processBoldText(
          `${framework.num} ${framework.title}`,
          margin,
          yPosition
        );
        yPosition += spacing.betweenItems;

        // Bullet points
        framework.points.forEach(point => {
          doc.text('•', margin + spacing.bulletIndent, yPosition);
          doc.text(point, margin + spacing.bulletIndent + spacing.subIndent, yPosition);
          yPosition += spacing.betweenItems;
        });
        
        yPosition += spacing.afterBulletList;
      });

      return yPosition;
    };

    // Update the addSectionContent function to use the new bold text processing
    const addSectionContent = (section, yPosition) => {
      // Section heading in green
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(fonts.sectionHeading);
      doc.setTextColor(...colors.green);
      doc.text(section.heading, margin, yPosition);
      yPosition += spacing.afterHeading;

      // Section description
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(fonts.body);
      doc.setTextColor(...colors.gray);
      doc.text('Understanding the transformation of fiscal sponsorship from its origins to contemporary applications:', 
        margin, yPosition);
      yPosition += spacing.afterSubheading;

      // Process content parts
      const contentParts = section.text.split('\n\n');
      
      contentParts.forEach(part => {
        if (part.includes('**Hybrid Entity Frameworks**')) {
          yPosition = processHybridFrameworks(yPosition);
        }
        else if (part.startsWith('**Historical Context**:')) {
          // Historical Context section
          doc.setFont('helvetica', 'bold');
          doc.text('Historical Context:', margin, yPosition);
          yPosition += spacing.afterSubheading;

          doc.setFont('helvetica', 'normal');
          const points = [
            'Origins in Adler & Colvin\'s foundational work',
            'Evolution of IRS interpretations',
            'Emergence of modern philanthropic needs'
          ];

          points.forEach(point => {
            doc.text('•', margin + 5, yPosition);
            doc.text(point, margin + spacing.bulletIndent, yPosition);
            yPosition += spacing.betweenItems;
          });
          yPosition += spacing.afterBulletList;
        }
        else if (part.startsWith('**Traditional Models Overview**:')) {
          // Traditional Models section
          doc.setFont('helvetica', 'bold');
          doc.text('Traditional Models Overview:', margin, yPosition);
          yPosition += spacing.afterSubheading;

          const models = [
            { num: '1.', title: 'Direct Project (Model A)', desc: 'Complete integration with sponsor' },
            { num: '2.', title: 'Independent Contractor (Model B)', desc: 'Project leadership as contractors' },
            { num: '3.', title: 'Pre-approved Grant (Model C)', desc: 'Independent project with fiscal oversight' },
            { num: '4.', title: 'Group Exemption (Model D)', desc: 'Umbrella structure for multiple projects' },
            { num: '5.', title: 'Supporting Organization (Model E)', desc: 'Separate entity alignment' },
            { num: '6.', title: 'Technical Assistance (Model F)', desc: 'Focused administrative support' }
          ];

          models.forEach(({ num, title, desc }) => {
            // Number with proper spacing
            doc.setFont('helvetica', 'normal');
            doc.text(num, margin, yPosition);

            // Model name without parentheses
            const modelName = title.split('(')[0].trim();
            const modelCode = '(' + title.split('(')[1];
            
            // Calculate positions
            const numberWidth = doc.getTextWidth(num + ' ');
            const modelNameWidth = doc.getTextWidth(modelName + ' ');
            
            // Model name
            doc.text(modelName, margin + numberWidth, yPosition);
            
            // Model code in same style
            doc.text(modelCode, margin + numberWidth + modelNameWidth, yPosition);
            
            // Description
            doc.text(desc, margin + numberWidth + modelNameWidth + doc.getTextWidth(modelCode + ' '), yPosition);
            
            yPosition += spacing.betweenItems;
          });

          yPosition += spacing.afterNumberedList;
        }
        else if (part.startsWith('**Modern Innovation**:')) {
          // Modern Innovation section
          doc.setFont('helvetica', 'bold');
          doc.text('Modern Innovation:', margin, yPosition);
          yPosition += spacing.afterSubheading;

          doc.setFont('helvetica', 'normal');
          const innovations = [
            'Hybrid structures combining multiple models',
            'Technology-enabled oversight systems',
            'Scaled compliance frameworks'
          ];

          innovations.forEach(innovation => {
            doc.text('•', margin + 5, yPosition);
            doc.text(innovation, margin + spacing.bulletIndent, yPosition);
            yPosition += spacing.betweenItems;
          });
          yPosition += spacing.afterBulletList;
        }
        else if (part.toLowerCase().includes('case study:')) {
          yPosition += spacing.paragraphSpacing;
          yPosition = addCaseStudyBox(part, yPosition);
          yPosition += spacing.paragraphSpacing;
        }
        else if (part.startsWith('**Advanced Control Systems**:')) {
          doc.setFont('helvetica', 'bold');
          doc.text('Advanced Control Systems:', margin, yPosition);
          yPosition += spacing.afterSubheading;

          const systems = [
            {
              title: 'Fund Segregation',
              points: [
                'Automated restriction enforcement',
                'Real-time fund tracking',
                'Multi-layer approval workflows'
              ]
            },
            // ... other systems
          ];

          systems.forEach(system => {
            doc.setFont('helvetica', 'bold');
            doc.text(`1. **${system.title}**:`, margin, yPosition);
            yPosition += spacing.betweenItems;

            doc.setFont('helvetica', 'normal');
            system.points.forEach(point => {
              doc.text('•', margin + spacing.bulletIndent, yPosition);
              doc.text(point, margin + spacing.bulletIndent + spacing.subIndent, yPosition);
              yPosition += spacing.betweenItems;
            });
            yPosition += spacing.afterBulletList;
          });
        }
        else {
          const lines = doc.splitTextToSize(part, contentWidth);
          lines.forEach(line => {
            processBoldText(line, margin, yPosition);
            yPosition += spacing.lineHeight;
          });
          yPosition += spacing.paragraphSpacing;
        }
      });

      return yPosition + spacing.betweenSections;
    };

    // Add case study box with styling
    const addCaseStudyBox = (text, yPosition) => {
      const boxPadding = 10;
      const boxHeight = 60;

      // Draw background with lighter color instead of alpha
      doc.setFillColor(240, 245, 255); // Light blue background
      doc.roundedRect(margin, yPosition, contentWidth, boxHeight, 5, 5, 'F');

      // Add case study content
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(fonts.caption);
      doc.setTextColor(...colors.blue);
      doc.text('CASE STUDY', margin + boxPadding, yPosition + boxPadding + 5);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(fonts.body);
      doc.setTextColor(...colors.gray);
      const lines = doc.splitTextToSize(text, contentWidth - (boxPadding * 2));
      doc.text(lines, margin + boxPadding, yPosition + boxPadding + 15);

      return yPosition + boxHeight + 10;
    };

    // Add appendices with styling
    const addAppendices = (yPosition) => {
      // Main Appendices heading in blue
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(fonts.sectionHeading);
      doc.setTextColor(...colors.blue);
      doc.text('Appendices', margin, yPosition);
      yPosition += 20;

      // Appendix sections
      const appendices = [
        {
          title: "Appendix 1: Compliance Framework Templates",
          content: [
            "AI-Enabled Charitable Purpose Validation",
            "Smart Contract Implementation Guide",
            "Automated Compliance Monitoring Systems",
            "Cross-Border Operations Protocols",
            "Quantum-Security Implementation"
          ]
        },
        {
          title: "Appendix 2: Risk Management Protocols",
          content: [
            "Advanced Threat Detection Systems",
            "Multi-Layer Authorization Frameworks",
            "Automated Audit Procedures",
            "Crisis Response Protocols",
            "Business Continuity Planning"
          ]
        },
        {
          title: "Appendix 3: Technology Integration Guide",
          content: [
            "Blockchain Implementation",
            "AI/ML Integration Framework",
            "IoT-Enabled Impact Tracking",
            "Smart Contract Development",
            "Quantum-Safe Security Protocols"
          ]
        }
      ];

      appendices.forEach((appendix) => {
        // Appendix title in green
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(fonts.subHeading);
        doc.setTextColor(...colors.green);
        doc.text(appendix.title, margin, yPosition);
        yPosition += 15;

        // Appendix content items with bullets
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(fonts.body);
        doc.setTextColor(...colors.gray);
        
        appendix.content.forEach(item => {
          // Add bullet point
          doc.text('•', margin + 5, yPosition);
          // Add content with proper indentation
          doc.text(item, margin + spacing.bulletIndent + 5, yPosition);
          yPosition += spacing.lineHeight;
        });
        
        yPosition += 15; // Space between appendix sections
      });

      return yPosition;
    };

    // Add advanced fund control section with diagrams
    const addAdvancedFundControl = (yPosition) => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(fonts.sectionHeading);
      doc.setTextColor(...colors.blue);
      doc.text('Advanced Fund Control Architecture', margin, yPosition);
      yPosition += 15;

      // Add fund flow diagram
      const diagramHeight = 80;
      doc.setFillColor(240, 245, 255); // Light blue background
      doc.roundedRect(margin, yPosition, contentWidth, diagramHeight, 5, 5, 'F');

      // Add diagram elements
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(fonts.caption);
      doc.setTextColor(...colors.blue);
      
      // Draw flow chart elements
      const boxWidth = 60;
      const boxHeight = 30;
      const startX = margin + 20;
      const centerY = yPosition + (diagramHeight / 2);

      // Draw boxes and arrows for fund flow
      drawFlowChartBox('Donor Input', startX, centerY - boxHeight/2, boxWidth, boxHeight);
      drawFlowChartBox('Validation Layer', startX + boxWidth + 40, centerY - boxHeight/2, boxWidth, boxHeight);
      drawFlowChartBox('Fund Control', startX + (boxWidth + 40) * 2, centerY - boxHeight/2, boxWidth, boxHeight);
      drawFlowChartBox('Program Output', startX + (boxWidth + 40) * 3, centerY - boxHeight/2, boxWidth, boxHeight);

      yPosition += diagramHeight + 20;

      // Add control mechanisms description
      const mechanisms = [
        {
          title: "Multi-Layer Authorization",
          points: [
            "AI-powered purpose validation",
            "Real-time compliance checks",
            "Dynamic approval workflows"
          ]
        },
        {
          title: "Automated Controls",
          points: [
            "Smart contract enforcement",
            "Blockchain fund tracking",
            "Real-time restriction monitoring"
          ]
        },
        {
          title: "Risk Management",
          points: [
            "Predictive analytics",
            "Automated deviation alerts",
            "Continuous compliance scanning"
          ]
        }
      ];

      mechanisms.forEach(mech => {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(fonts.subHeading);
        doc.setTextColor(...colors.green);
        doc.text(mech.title, margin, yPosition);
        yPosition += 10;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(fonts.body);
        doc.setTextColor(...colors.gray);
        mech.points.forEach(point => {
          doc.text(`• ${point}`, margin + 10, yPosition);
          yPosition += 8;
        });
        yPosition += 10;
      });

      return yPosition;
    };

    // Add Professional Cause Manager framework section
    const addCauseManagerFramework = (yPosition) => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(fonts.sectionHeading);
      doc.setTextColor(...colors.blue);
      doc.text('Professional Cause Manager Excellence Framework', margin, yPosition);
      yPosition += 15;

      // Add role matrix diagram with light background
      const matrixHeight = 100;
      doc.setFillColor(240, 245, 255);
      doc.roundedRect(margin, yPosition, contentWidth, matrixHeight, 5, 5, 'F');

      // Draw matrix elements showing responsibilities and authority
      const matrix = [
        ["Responsibility", "Authority", "Oversight"],
        ["Program Direction", "Fund Requests", "Compliance"],
        ["Impact Measurement", "Budget Management", "Reporting"],
        ["Stakeholder Relations", "Resource Allocation", "Risk Management"]
      ];

      const cellWidth = contentWidth / 3;
      const cellHeight = matrixHeight / 4;

      // Draw grid lines
      doc.setDrawColor(...colors.blue);
      matrix.forEach((row, rowIndex) => {
        row.forEach((cell, colIndex) => {
          const x = margin + (cellWidth * colIndex);
          const y = yPosition + (cellHeight * rowIndex);
          
          // Draw cell borders
          doc.rect(x, y, cellWidth, cellHeight);
          
          // Add cell content
          doc.setFont('helvetica', rowIndex === 0 ? 'bold' : 'normal');
          doc.setFontSize(fonts.caption);
          doc.setTextColor(...colors.gray);
          
          const textX = x + 5;
          const textY = y + (cellHeight / 2) + 4;
          doc.text(cell, textX, textY);
        });
      });

      yPosition += matrixHeight + 20;

      // Add framework components
      const components = [
        {
          title: "Professional Standards",
          content: "Comprehensive framework for Cause Manager certification and ongoing professional development."
        },
        {
          title: "Operational Authority",
          content: "Clear delineation of decision-making power and responsibility boundaries."
        },
        {
          title: "Risk Management",
          content: "Advanced protocols for protecting both the Cause Manager and the fiscal sponsor."
        }
      ];

      components.forEach(comp => {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(fonts.subHeading);
        doc.setTextColor(...colors.green);
        doc.text(comp.title, margin, yPosition);
        yPosition += 10;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(fonts.body);
        doc.setTextColor(...colors.gray);
        const lines = doc.splitTextToSize(comp.content, contentWidth - 20);
        lines.forEach(line => {
          doc.text(line, margin + 10, yPosition);
          yPosition += 8;
        });
        yPosition += 10;
      });

      return yPosition;
    };

    // Helper function for drawing flow chart boxes
    const drawFlowChartBox = (text, x, y, width, height) => {
      // Use lighter blue for box background
      doc.setFillColor(245, 250, 255);
      doc.roundedRect(x, y, width, height, 3, 3, 'F');

      // Add border
      doc.setDrawColor(...colors.blue);
      doc.roundedRect(x, y, width, height, 3, 3, 'D');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(fonts.caption);
      doc.setTextColor(...colors.blue);
      
      const textWidth = doc.getTextWidth(text);
      const textX = x + (width - textWidth) / 2;
      const textY = y + height / 2 + 4; // Adjust for vertical centering
      
      doc.text(text, textX, textY);
    };

    // Update page break check in content processing
    const checkPageBreak = (yPosition, requiredSpace = 50) => {
      if (yPosition + requiredSpace > pageHeight - footerHeight - 30) {
        doc.addPage();
        const pageNumber = doc.internal.getNumberOfPages();
        addHeader();
        addFooter(pageNumber);
        return headerHeight + 20;
      }
      return yPosition;
    };

    // Generate PDF with enhanced error handling
    try {
      console.log('Starting document generation...');
      
      // Add title page
      addHeader();
      addFooter(1);
      addTitlePage();
      console.log('Title page added');

      // Add table of contents
      doc.addPage();
      let pageNumber = 2;
      addHeader();
      addFooter(pageNumber);
      let yPosition = headerHeight + 20;
      yPosition = addTableOfContents(yPosition);
      console.log('Table of contents added');

      // Add executive summary
      doc.addPage();
      pageNumber++;
      addHeader();
      addFooter(pageNumber);
      yPosition = headerHeight + 20;
      yPosition = addExecutiveSummary(yPosition);
      console.log('Executive summary added');

      // Process main content with progress logging
      console.log(`Processing ${fiscalSponsorshipDefinitiveGuide.sections.length} sections...`);
      fiscalSponsorshipDefinitiveGuide.sections.forEach((section, index) => {
        console.log(`Processing section ${index + 1}: ${section.heading}`);
        if (yPosition + 100 > pageHeight - footerHeight) {
          doc.addPage();
          pageNumber++;
          addHeader();
          addFooter(pageNumber);
          yPosition = headerHeight + 20;
        }
        yPosition = addSectionContent(section, yPosition);
      });

      // Add appendices
      console.log('Adding appendices...');
      doc.addPage();
      pageNumber++;
      addHeader();
      addFooter(pageNumber);
      yPosition = headerHeight + 20;
      yPosition = addAppendices(yPosition);

      // Add advanced sections
      console.log('Adding advanced sections...');
      doc.addPage();
      pageNumber++;
      addHeader();
      addFooter(pageNumber);
      yPosition = headerHeight + 20;
      yPosition = addAdvancedFundControl(yPosition);

      doc.addPage();
      pageNumber++;
      addHeader();
      addFooter(pageNumber);
      yPosition = headerHeight + 20;
      yPosition = addCauseManagerFramework(yPosition);

      // Add CTA before end of document
      yPosition = checkPageBreak(yPosition, 80);
      yPosition = addCallToAction(yPosition);

      console.log('PDF generation completed successfully');
      return doc;
    } catch (error) {
      console.error('Error during PDF generation:', error);
      throw new Error(`PDF generation failed: ${error.message}`);
    }
  } catch (error) {
    console.error('Fatal error in generateFiscalSponsorshipDefinitiveGuide:', error);
    throw error;
  }
};

// Export with additional error handling
export const generateDefinitiveGuide = async () => {
  try {
    console.log('Starting Definitive Guide generation');
    const doc = await generateFiscalSponsorshipDefinitiveGuide();
    if (!doc) {
      throw new Error('PDF generation failed - null document returned');
    }
    console.log('Guide generated successfully');
    return doc;
  } catch (error) {
    console.error('Error in generateDefinitiveGuide:', error);
    throw error;
  }
}; 